import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { setFullLocale } from '../utils/utils'
import { useRouter } from 'next/router'
import {
  Box,
  Container,
  Fade,
  Grid,
  makeStyles,
  Typography,
  useTheme,
  createStyles,
} from '@material-ui/core'
import { getTranslations } from '../i18n'
import TextField from '../components/TextField/TextField'
import Button from '../components/Button/Button'
import { putResetPassword } from '../redux/actions/user'
import Loader from '../components/Loader/Loader'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorIcon from '@material-ui/icons/Error'
import { createClient, getSingleDocument } from '../prismicio'

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      minHeight: '80vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },
    logo: {
      display: 'block',
      maxHeight: 80,
      width: 'auto',
      margin: 'auto',
    },
  })
)

type Tprops = {
  doc?: any
  putResetPassword: any
}

const mapStateToProps = (state) => ({
  user: state.user,
})

const mapDispatchToProps = {
  putResetPassword: putResetPassword,
}

const ResetPassword = ({ doc, putResetPassword }: Tprops) => {
  const router = useRouter()
  const { t } = useTranslation()
  const theme = useTheme()
  const classes = useStyles()
  const [newPassword, setNewPassword] = useState('')
  const [repeatPassword, setRepeatPassword] = useState('')
  const [error, setError] = useState(false)
  const [success, setSuccess] = useState(false)
  const [disabled, setDisabled] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (newPassword === '' || repeatPassword === '') {
      setDisabled(true)
    } else {
      if (newPassword === repeatPassword) {
        setDisabled(false)
      } else {
        setDisabled(true)
      }
    }
  }, [disabled, newPassword, repeatPassword])

  useEffect(() => {
    if (router.asPath === router.route) {
      router.push('/')
    }
  }, [])

  const submitForm = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError(false)

    if (router.asPath !== router.route) {
      const token = (router.query.url as string)
        ?.split('/')
        .slice(-1)[0]
        .split('?')
        .slice(-2)[0]

      if (newPassword === repeatPassword) {
        const data = {
          first: newPassword,
          second: repeatPassword,
        }

        const resetPwd = await putResetPassword(data, token)

        if (resetPwd.type === 'RESET_PASSWORD_SUCCESS') {
          setSuccess(true)
          setError(false)
          setIsLoading(false)
          setTimeout(function () {
            router.push('/register')
          }, 3000)
        } else if (resetPwd.type === 'RESET_PASSWORD_FAILURE') {
          setSuccess(false)
          setError(true)
          setIsLoading(false)
        }
      }
    }
  }

  return (
    <Container maxWidth="md">
      <div className={classes.root}>
        <Box mt={6}>
          <img
            className={classes.logo}
            src={
              doc?.data?.logo
                ? doc?.data?.logo?.url
                : '/images/logo_default_grey.svg'
            }
            alt=""
          />
        </Box>
        <Box mt={6}>
          <Typography align="center" variant="h3">
            {t('texts:account:password_reset:title')}
          </Typography>
          <Typography align="center" variant="body1">
            {t('texts:account:password_reset:description')}
          </Typography>
        </Box>
        <Box mt={6}>
          <form>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <TextField
                  type="password"
                  name="new-password"
                  placeholder={t('texts:account:password')}
                  value={newPassword}
                  required
                  onChange={setNewPassword}
                  autofocus
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  type="password"
                  name="repeat-password"
                  placeholder={t('texts:account:repeat_password')}
                  value={repeatPassword}
                  required
                  onChange={setRepeatPassword}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography align="center" component="div">
                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    disabled={disabled || isLoading}
                    onClick={submitForm}
                  >
                    {isLoading && <Loader />}
                    <Fade in={!isLoading}>
                      <span>{t('texts:account:password_button')}</span>
                    </Fade>
                  </Button>
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Fade in={error}>
                  <Typography
                    variant="body1"
                    color="error"
                    align="center"
                    component="div"
                  >
                    <Grid container spacing={1} justify="center" wrap="nowrap">
                      <Grid item>
                        <ErrorIcon />
                      </Grid>
                      <Grid item>
                        <Typography align="left">
                          {t('texts:account:password_reset:error')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Typography>
                </Fade>
                <Fade in={success}>
                  <Typography
                    variant="body1"
                    align="center"
                    component="div"
                    style={{ color: theme.palette.success.main }}
                  >
                    <Grid container spacing={1} justify="center" wrap="nowrap">
                      <Grid item>
                        <CheckCircleIcon />
                      </Grid>
                      <Grid item>
                        <Typography align="left">
                          {t('texts:account:password_reset:success')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Typography>
                </Fade>
              </Grid>
            </Grid>
          </form>
        </Box>
      </div>
    </Container>
  )
}

export async function getStaticProps({ locale, previewData }) {
  const client = createClient({ previewData })
  const lang = setFullLocale(locale)

  const general = await getSingleDocument(client, lang, 'general')

  return {
    props: {
      doc: general,
      i18nResources: await getTranslations(locale, ['texts']),
    },
    revalidate: 300,
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ResetPassword)
